/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { meetingsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'
import AvailabilityPanel from './AvailabilityPanel'
import PersonInput, { ME, assignable, useMembers } from '../records/PersonInput'
import { fromInputDateTime, toInputDateTime } from '../tasks/taskModel'
import {
  DURATIONS, MEETING_REMINDERS, RESULTS, STATUS_LABEL, defaultStart, durationLabel, isEmail, minutesBetween, plusMinutes,
} from './meetingModel'

/**
 * Plan a meeting, log a call, or open one to change it, mark it held or cancel it.
 * New ones: {@code kind} MEETING | CALL, optional {@code recordRef} ({ id, title }) and {@code start} (input value).
 */
const MeetingDialog = ({ open, onClose, meeting, kind: initialKind = 'MEETING', recordRef, start }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { data: members = [] } = useMembers()
  const isNew = !meeting
  const canEdit = isNew || meeting.canEdit
  const [kind, setKind] = useState(meeting?.kind || initialKind)
  const [title, setTitle] = useState(meeting?.title || '')
  const [startsAt, setStartsAt] = useState(meeting ? toInputDateTime(meeting.startsAt) : start || defaultStart(initialKind))
  const [duration, setDuration] = useState(meeting ? minutesBetween(meeting.startsAt, meeting.endsAt) : initialKind === 'CALL' ? 10 : 30)
  const [location, setLocation] = useState(meeting?.location || '')
  const [videoLink, setVideoLink] = useState(meeting?.videoLink || '')
  const [owner, setOwner] = useState(meeting?.owner?.id || ME)
  const [invited, setInvited] = useState(meeting?.members?.map((m) => m.id) || [])
  const [guests, setGuests] = useState(meeting?.guestEmails || [])
  const [notes, setNotes] = useState(meeting?.notes || '')
  const [direction, setDirection] = useState(meeting?.callDirection || 'OUTGOING')
  const [result, setResult] = useState(meeting?.callResult || 'CONNECTED')
  const [reminder, setReminder] = useState(meeting?.remindMinutesBefore ?? (isNew ? 10 : ''))
  const [sendInvites, setSendInvites] = useState(true)
  const [outcome, setOutcome] = useState('')
  const [closing, setClosing] = useState(false)   // the "mark as held" step
  const [clashSeen, setClashSeen] = useState(false) // saving over a clash takes a second click

  const done = () => {
    queryClient.invalidateQueries({ queryKey: [tenantId, 'meetings'] })
    queryClient.invalidateQueries({ queryKey: [tenantId, 'calendar'] })
    queryClient.invalidateQueries({ queryKey: [tenantId, 'records', 'activity'] })
    onClose()
  }
  const call = kind === 'CALL'
  // who's busy then: the organiser and invited teammates (CRM meetings and their connected calendars)
  const checkable = !call && canEdit && !!startsAt && (isNew || meeting.status === 'PLANNED')
  const availabilityBody = checkable ? {
    startsAt: fromInputDateTime(startsAt),
    endsAt: fromInputDateTime(plusMinutes(startsAt, duration)),
    ownerUserId: owner && owner !== ME ? owner : null,
    memberUserIds: invited,
    meetingId: meeting?.id || null,
  } : null
  const availability = useQuery({
    queryKey: [tenantId, 'availability', availabilityBody],
    queryFn: () => meetingsAPI.availability(availabilityBody),
    enabled: !!availabilityBody,
    staleTime: 60_000,
    retry: false,
    placeholderData: (prev) => prev,
  })
  const conflicts = checkable ? availability.data?.conflicts || [] : []
  const clashKey = JSON.stringify(conflicts.map((c) => [c.user.id, c.start, c.end]))
  useEffect(() => setClashSeen(false), [clashKey])
  const submit = () => {
    if (conflicts.length && !clashSeen) {
      setClashSeen(true)   // the warning is on screen; the next click plans it anyway
      return
    }
    save.mutate()
  }
  const durations = DURATIONS.includes(duration) ? DURATIONS : [...DURATIONS, duration].sort((a, b) => a - b)

  const save = useMutation({
    mutationFn: () => {
      const body = {
        kind,
        title: title.trim(),
        startsAt: fromInputDateTime(startsAt),
        endsAt: fromInputDateTime(plusMinutes(startsAt, duration)),
        recordId: isNew ? recordRef?.id : meeting.record?.id,
        location: location.trim() || null,
        videoLink: videoLink.trim() || null,
        ownerUserId: owner && owner !== ME ? owner : null,
        memberUserIds: invited,
        guestEmails: guests,
        notes: notes.trim() || null,
        status: isNew ? undefined : meeting.status,
        callDirection: call ? direction : null,
        callResult: call ? result : null,
        remindMinutesBefore: !call && reminder !== '' ? Number(reminder) : null,
        sendInvites,
      }
      return isNew ? meetingsAPI.create(body) : meetingsAPI.update(meeting.id, body)
    },
    onSuccess: (m) => {
      notify(isNew ? (call ? 'Call logged' : m.calendar ? 'Meeting planned and added to your calendar' : 'Meeting planned') : 'Saved')
      done()
    },
  })
  const status = useMutation({
    mutationFn: (s) => meetingsAPI.setStatus(meeting.id, s, s === 'DONE' ? outcome.trim() : null),
    onSuccess: (_, s) => { notify(s === 'DONE' ? 'Marked as held' : s === 'CANCELLED' ? 'Meeting cancelled' : 'Back to planned'); done() },
  })
  const remove = useMutation({ mutationFn: () => meetingsAPI.remove(meeting.id), onSuccess: () => { notify('Deleted'); done() } })
  const error = save.error || status.error || remove.error

  const memberOptions = assignable(members).map((m) => m.userId)
  const memberLabel = (id) => {
    const m = members.find((x) => x.userId === id)
    return m?.name || m?.email || meeting?.members?.find((x) => x.id === id)?.title || 'Member'
  }
  const record = recordRef || meeting?.record
  const valid = title.trim() && startsAt && guests.every(isEmail) && (!videoLink.trim() || /^https:\/\/\S+$/i.test(videoLink.trim()))

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ component: 'form', onSubmit: (e) => { e.preventDefault(); e.stopPropagation(); if (valid && canEdit) submit() } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flexGrow: 1 }}>{isNew ? (call ? 'Log a call' : 'Plan a meeting') : call ? 'Call' : 'Meeting'}</Box>
        {!isNew && <Chip size="small" label={STATUS_LABEL[meeting.status]} color={meeting.status === 'CANCELLED' ? 'default' : meeting.status === 'DONE' ? 'success' : 'primary'} variant="outlined" />}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}
          {meeting?.syncError && <Alert severity="warning">{meeting.syncError}. Invites were sent by email instead.</Alert>}
          {record && <Typography variant="body2" color="text.secondary">With: <strong>{record.title}</strong></Typography>}
          {isNew && (
            <ToggleButtonGroup size="small" exclusive value={kind} onChange={(_, v) => v && setKind(v)}>
              <ToggleButton value="MEETING">Meeting</ToggleButton>
              <ToggleButton value="CALL">Call</ToggleButton>
            </ToggleButtonGroup>
          )}
          <TextField label={call ? 'What was it about' : 'Title'} required autoFocus={isNew} value={title} disabled={!canEdit}
            onChange={(e) => setTitle(e.target.value)} inputProps={{ maxLength: 300 }}
            placeholder={call ? 'e.g. Follow-up on the quote' : 'e.g. Product demo'} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={call ? 'When' : 'Starts'} type="datetime-local" required value={startsAt} disabled={!canEdit}
              onChange={(e) => setStartsAt(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
            <TextField select label="Duration" value={duration} disabled={!canEdit} onChange={(e) => setDuration(Number(e.target.value))} sx={{ flex: 1 }}>
              {durations.map((d) => <MenuItem key={d} value={d}>{durationLabel(d)}</MenuItem>)}
            </TextField>
          </Stack>
          {checkable && <AvailabilityPanel query={availability} onPick={(start) => setStartsAt(toInputDateTime(start))} />}
          {clashSeen && conflicts.length > 0 && (
            <Typography variant="body2" color="warning.main">Someone is busy then. Pick a suggested time, or click “Plan anyway”.</Typography>
          )}
          {call ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Direction" value={direction} disabled={!canEdit} onChange={(e) => setDirection(e.target.value)} sx={{ flex: 1 }}>
                <MenuItem value="OUTGOING">Outgoing</MenuItem>
                <MenuItem value="INCOMING">Incoming</MenuItem>
              </TextField>
              <TextField select label="Result" value={result} disabled={!canEdit} onChange={(e) => setResult(e.target.value)} sx={{ flex: 1 }}>
                {RESULTS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </TextField>
            </Stack>
          ) : (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Location" value={location} disabled={!canEdit} onChange={(e) => setLocation(e.target.value)} sx={{ flex: 1 }} inputProps={{ maxLength: 500 }} />
                <TextField label="Video link" value={videoLink} disabled={!canEdit} onChange={(e) => setVideoLink(e.target.value)} sx={{ flex: 1 }}
                  placeholder="https://meet.google.com/…" error={!!videoLink.trim() && !/^https:\/\/\S+$/i.test(videoLink.trim())} inputProps={{ maxLength: 1000 }} />
              </Stack>
              <Autocomplete multiple options={memberOptions} value={invited} disabled={!canEdit} getOptionLabel={memberLabel}
                onChange={(_, v) => setInvited(v)} renderInput={(p) => <TextField {...p} label="Teammates" placeholder="Invite colleagues" />} />
              <Autocomplete multiple freeSolo options={[]} value={guests} disabled={!canEdit}
                onChange={(_, v) => setGuests(v.map((x) => x.trim().toLowerCase()).filter(Boolean))}
                renderTags={(v, getTagProps) => v.map((g, i) => <Chip {...getTagProps({ index: i })} key={g} size="small" label={g} color={isEmail(g) ? 'default' : 'error'} />)}
                renderInput={(p) => <TextField {...p} label="Guests" placeholder="Type an email and press Enter" helperText="People outside your team, e.g. the customer" />} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField select label="Reminder" value={reminder} disabled={!canEdit} onChange={(e) => setReminder(e.target.value)} sx={{ flex: 1 }}
                  SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                  {MEETING_REMINDERS.map((r) => <MenuItem key={String(r.value)} value={r.value}>{r.label}</MenuItem>)}
                </TextField>
                <Box sx={{ flex: 1 }}>
                  <PersonInput label="Organiser" value={owner} onChange={setOwner} initialRef={meeting?.owner} required disabled={!canEdit} />
                </Box>
              </Stack>
              {(guests.length > 0 || invited.length > 0) && canEdit && (
                <FormControlLabel control={<Checkbox checked={sendInvites} onChange={(e) => setSendInvites(e.target.checked)} />}
                  label={<Typography variant="body2">Send calendar invites{meeting?.calendar ? ' (through the organiser’s calendar)' : ''}</Typography>} />
              )}
            </>
          )}
          <TextField label={call ? 'Notes' : 'Agenda and notes'} multiline minRows={2} maxRows={10} value={notes} disabled={!canEdit}
            onChange={(e) => setNotes(e.target.value)} inputProps={{ maxLength: 20000 }} />
          {closing && (
            <TextField label="What came of it" multiline minRows={2} autoFocus value={outcome} onChange={(e) => setOutcome(e.target.value)}
              helperText="Added to the notes" />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {!isNew && canEdit && (
          <Box sx={{ mr: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {meeting.status === 'PLANNED' && !closing && <Button onClick={() => setClosing(true)}>Mark as held</Button>}
            {closing && <Button variant="contained" color="success" disabled={status.isPending} onClick={() => status.mutate('DONE')}>Save as held</Button>}
            {meeting.status === 'PLANNED' && !call && <Button color="warning" disabled={status.isPending} onClick={() => status.mutate('CANCELLED')}>Cancel meeting</Button>}
            {meeting.status !== 'PLANNED' && !call && <Button disabled={status.isPending} onClick={() => status.mutate('PLANNED')}>Back to planned</Button>}
            <Button color="error" disabled={remove.isPending} onClick={() => remove.mutate()}>Delete</Button>
          </Box>
        )}
        <Button onClick={onClose}>{canEdit ? 'Close' : 'Done'}</Button>
        {canEdit && !closing && (
          <Button type="submit" variant="contained" disabled={!valid || save.isPending}>
            {save.isPending ? 'Saving…' : clashSeen && conflicts.length ? (isNew ? 'Plan anyway' : 'Save anyway') : isNew ? (call ? 'Log call' : 'Plan meeting') : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default MeetingDialog
